import React, { useState, useEffect, useContext } from "react";
import {
  HomeQuery,
  HeroHomeFragment,
  CountUpSliceFragment,
  LocalesSliceFragment,
  SeoSliceFragment,
} from "../../types/graphql";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import Layout from "../../components/layout/layout";
import HeroBifurcated from "../../components/hero-bifurcated/hero-bifurcated";
import {
  ContentWrapper,
  FullWidthSliceWrapper,
  NarrowSliceWrapper,
} from "./home.styled";
import { graphql, PageProps } from "gatsby";
import Helmet from "react-helmet";
import { WebSite } from "schema-dts";
import { helmetJsonLdProp } from "react-schemaorg";
import LogoMetaData from "./logo-meta-data";
import AdditionalMetaData from "./additional-meta-data";
import { ILocalesProps } from "../../components/locales/locales";
import { ISeoProps } from "../../components/seo/seo";
import * as prismicHelper from "@prismicio/helpers";
import { IPageContext, ILocationStateProps } from "../../types/types";
import {
  SharedSliceType,
  SharedSliceFragment,
  SharedSlice,
  WrapperType,
} from "../../components/shared-slice/shared-slice";

export const query = graphql`
  query Home {
    prismicHome {
      _previewable
      lang
      data {
        body1 {
          ...HeroHome
          ...CountUpSlice
          ...FeaturedCustomersSlice
          ...BenefitQuadrantSlice
          ...MediaMarqueeSlice
          ...TestimonialsSlice
          ...TextAndImageSlice
          ...SimpleInlineCtaSlice
          ...ContentPostSlice
          ...ResourcesPostsSlice
          ...NewsletterSignupSlice
          ...CenteredFullWidthCtaSlice
          ...LatestCapabilitiesSlice
          ...QueriedResourcePostsSlice
          ...ImageAndCaptionSlice
          ...RichTextSlice
          ...AdvancedTableSlice
          ...CapabilityListSlice
          ...CarbonOffsetSlice
          ...FullWidthCtaSlice
          ...YoutubeVideoSlice
          ...PullQuoteSlice
          ...NestedContentSlice
          ...ImageAndListSlice
          ...ImageCarouselSlice
          ...InlineCtaSlice
          ...ImageGridSlice
          ...XometryEverywhereSlice
          ...ThreeSimpleCirclesSlice
          ...ThreeSimpleCardsSlice
          ...TestimonialSlice
          ...TableSlice
          ...TextAndModelFileSlice
          ...QuoteUploadComponentSlice
          ...LinkGridSlice
          ...AdvancedSliceSlice
          ...ContentCarouselSlice
        }
        body {
          ...SeoSlice
          ...LocalesSlice
        }
      }
    }
    ...HeroHomeImages
  }
`;

const Home = (
  props: PageProps<HomeQuery, IPageContext, ILocationStateProps>
) => {
  const home = props.data.prismicHome;
  const homeData = props.data.prismicHome?.data;
  const slices = homeData?.body1;
  const seoSlices = homeData?.body && homeData?.body;
  const showAnnouncementBanner = true;

  let pageLocale: ILocalesProps = {};
  let seo: ISeoProps = {};

  var hero = (
    slices?.find((slice) => {
      if ("slice_type" in slice && slice?.slice_type == "home_hero") {
        return slice;
      }
    }) as HeroHomeFragment
  )?.primary;

  seoSlices?.forEach((slice) => {
    if (slice?.slice_type == "page_locales") {
      const pageLocales = slice as LocalesSliceFragment;
      pageLocale = {
        ...pageLocales,
        northAmericaHref: props.location.href,
      };
    } else if (slice?.slice_type == "seo_") {
      const seoSlice = slice as SeoSliceFragment;
      seo = {
        meta_title: seoSlice?.primary?.meta_title,
        meta_description: seoSlice?.primary?.meta_description,
        title: hero?.title,
        description:
          hero?.subtitle && prismicHelper.asText(hero?.subtitle.richText),
        image: hero?.image?.url,
        lang: home?.lang,
      };
    }
  });

  const SiteLinksSearchBoxTag = () => (
    <Helmet
      script={[
        helmetJsonLdProp<WebSite>({
          "@context": "https://schema.org",
          "@type": "WebSite",
          url: "https://www.xometry.com/",
          potentialAction: {
            "@type": "SearchAction",
            target: {
              "@type": "EntryPoint",
              urlTemplate:
                "https://www.xometry.com/search/?query={search_term_string}",
            },
            // @ts-ignore "query-input" is a custom field for Google's Search
            // Explanation: https://developers.google.com/search/docs/advanced/structured-data/sitelinks-searchbox#website
            "query-input": "required name=search_term_string",
          },
        }),
      ]}
    />
  );

  return (
    <Layout
      seo={seo}
      isGuttered={false}
      showAnnouncementBanner={showAnnouncementBanner}
      locales={pageLocale}
      bgGradient={true}
    >
      <SiteLinksSearchBoxTag />
      <LogoMetaData />
      <AdditionalMetaData />
      {slices?.map((slice, index) => {
        if (!("slice_type" in slice)) return;

        if (slice?.slice_type == "featured_customers") {
          slice.slice_type = SharedSliceType.home_featured_customers;
        }

        if (slice?.slice_type == "home_hero") {
          return (
            <HeroBifurcated
              key={index}
              heroHome={slice as HeroHomeFragment}
              heroHomeImages={props.data}
            />
          );
        } else if (slice.slice_type && slice.slice_type in SharedSliceType) {
          return (
            <ContentWrapper>
              <SharedSlice
                context={props.pageContext}
                index={index}
                key={index}
                Wrappers={{
                  [WrapperType.narrow]: NarrowSliceWrapper,
                  [WrapperType.fullWidth]: FullWidthSliceWrapper,
                }}
                slice={slice as SharedSliceFragment}
                slice_type={
                  SharedSliceType[
                    slice.slice_type as keyof typeof SharedSliceType
                  ]
                }
              />
            </ContentWrapper>
          );
        }
      })}
    </Layout>
  );
};

export default withPrismicPreview(Home);
